import { useState } from "react";
import { Box, Typography, Button, alpha } from "@mui/material";
import { Add, VisibilityOff, Visibility } from "@mui/icons-material";
import { useRecordContext } from "react-admin";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useEndorsements, formatValidPeriod } from "../hooks/useEndorsements";
import type { EndorsementListItem, Policy } from "@/types";
import { UIComponentSize } from "@/types/shared/uiComponents";

interface PolicyEndorsementsSectionProps {
  userCanEdit: boolean;
  onEndorsementSelect: (endorsementId: string) => void;
  onAddEndorsement?: () => void;
  activeEndorsementId: string | null;
  isDrawerOpen: boolean;
}

/**
 * Unified endorsement list for the Policy endorsements tab.
 * Renders a single bordered table with inline category group headers,
 * replacing the previous stacked-accordions layout for a cleaner data-table feel.
 *
 * Row clicks trigger the side drawer via onEndorsementSelect.
 *
 * UX: Inactive endorsements are hidden by default. A "Show inactive" toggle reveals them.
 * Empty categories are hidden entirely when inactive rows are hidden.
 */
export const PolicyEndorsementsSection = ({
  userCanEdit,
  onEndorsementSelect,
  onAddEndorsement,
  activeEndorsementId,
}: PolicyEndorsementsSectionProps) => {
  const { theme, isDarkMode } = useAppTheme();
  const policyRecord = useRecordContext<Policy>();
  const policyId = policyRecord?.id as string;
  const [showInactive, setShowInactive] = useState(false);

  const { isLoading, getByCategory, activeCount, categories } = useEndorsements(
    policyId,
    policyRecord?.status === "active"
  );

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>
        Loading endorsements...
      </Typography>
    );
  }

  // Build a flat list of rows: category headers interleaved with endorsement rows
  const rows: FlatRow[] = [];
  for (const category of categories) {
    const allEndorsements = getByCategory(category.key);
    const visibleEndorsements = showInactive ? allEndorsements : allEndorsements.filter((e) => e.is_currently_active);

    // Skip empty categories entirely
    if (visibleEndorsements.length === 0) continue;

    const enabledCount = allEndorsements.filter((e) => e.is_currently_active).length;

    rows.push({
      type: "group-header",
      key: `header-${category.key}`,
      label: category.label,
      enabledCount,
      totalCount: allEndorsements.length,
    });

    for (const endorsement of visibleEndorsements) {
      rows.push({ type: "endorsement", key: endorsement.id, endorsement });
    }
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="span"
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
              bgcolor: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[100],
              px: 1,
              py: 0.125,
              borderRadius: 2.5,
              fontWeight: 500,
            }}
          >
            {activeCount} active
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          <Button
            size="small"
            onClick={() => setShowInactive((prev) => !prev)}
            startIcon={showInactive ? <VisibilityOff sx={{ fontSize: 14 }} /> : <Visibility sx={{ fontSize: 14 }} />}
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              textTransform: "none",
              color: "text.secondary",
              py: 0.25,
              px: 1,
            }}
          >
            {showInactive ? "Hide inactive" : "Show inactive"}
          </Button>
          {userCanEdit && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add sx={{ fontSize: 12 }} />}
              onClick={onAddEndorsement}
              sx={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "none", py: 0.25, px: 1.5 }}
            >
              Add Endorsement
            </Button>
          )}
        </Box>
      </Box>

      {/* Unified table */}
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {/* Column headers */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 100px 1fr",
            px: 2.5,
            py: 1,
            bgcolor: isDarkMode ? theme.palette.grey[900] : theme.palette.grey[50],
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {["Endorsement", "Status", "Valid Period"].map((header) => (
            <Typography
              key={header}
              variant="caption"
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.03em",
                color: "text.disabled",
                lineHeight: 1.5,
              }}
            >
              {header}
            </Typography>
          ))}
        </Box>

        {/* Rows */}
        {rows.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.disabled">
              No endorsements to display.
            </Typography>
          </Box>
        ) : (
          rows.map((row, i) => {
            if (row.type === "group-header") {
              return (
                <GroupHeaderRow
                  key={row.key}
                  label={row.label}
                  enabledCount={row.enabledCount}
                  totalCount={row.totalCount}
                  isFirst={i === 0}
                  theme={theme}
                  isDarkMode={isDarkMode}
                />
              );
            }

            const isSelected = activeEndorsementId === row.endorsement.id;
            const isLastInTable = i === rows.length - 1;
            // Check if next row is a group header (meaning this row is last in its group)
            const nextRow = rows[i + 1];
            const isLastInGroup = !nextRow || nextRow.type === "group-header";

            return (
              <EndorsementRow
                key={row.key}
                endorsement={row.endorsement}
                isSelected={isSelected}
                showBottomBorder={!isLastInTable}
                isLastInGroup={isLastInGroup}
                onClick={() => onEndorsementSelect(row.endorsement.id)}
                theme={theme}
              />
            );
          })
        )}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

type FlatRow =
  | { type: "group-header"; key: string; label: string; enabledCount: number; totalCount: number }
  | { type: "endorsement"; key: string; endorsement: EndorsementListItem };

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

/** Inline category group header — visually distinct divider row within the table */
const GroupHeaderRow = ({
  label,
  enabledCount,
  totalCount,
  isFirst,
  theme,
  isDarkMode,
}: {
  label: string;
  enabledCount: number;
  totalCount: number;
  isFirst: boolean;
  theme: ReturnType<typeof useAppTheme>["theme"];
  isDarkMode: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2.5,
      py: 1.25,
      bgcolor: alpha(theme.palette.primary.main, 0.2),
      borderTop: isFirst ? "none" : `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    }}
  >
    <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ fontSize: "0.8125rem" }}>
      {label}
    </Typography>
    <Box
      component="span"
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 500,
        color: "text.disabled",
        bgcolor: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[100],
        px: 0.75,
        py: 0.125,
        borderRadius: 1,
      }}
    >
      {enabledCount} / {totalCount}
    </Box>
  </Box>
);

/** Single endorsement row within the unified table */
const EndorsementRow = ({
  endorsement,
  isSelected,
  showBottomBorder,
  isLastInGroup,
  onClick,
  theme,
}: {
  endorsement: EndorsementListItem;
  isSelected: boolean;
  showBottomBorder: boolean;
  isLastInGroup: boolean;
  onClick: () => void;
  theme: ReturnType<typeof useAppTheme>["theme"];
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: "grid",
      gridTemplateColumns: "2fr 100px 1fr",
      px: 2.5,
      py: 2,
      cursor: "pointer",
      bgcolor: isSelected ? "action.selected" : "transparent",
      borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : "3px solid transparent",
      // Lighter divider between rows in the same group; no border if last in group (group header provides it)
      borderBottom: isLastInGroup || !showBottomBorder ? "none" : `1px solid ${theme.palette.divider}`,
      transition: "background-color 0.15s",
      "&:hover": { bgcolor: isSelected ? "action.selected" : "action.hover" },
    }}
  >
    {/* Endorsement label */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" fontWeight={500}>
        {endorsement.label}
      </Typography>
    </Box>

    {/* Status */}
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <StatusChip
        label={endorsement.is_currently_active ? "Active" : "Inactive"}
        color={endorsement.is_currently_active ? "success" : "default"}
        size={UIComponentSize.Small}
      />
    </Box>

    {/* Valid period */}
    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
      {formatValidPeriod(endorsement.valid_period_start, endorsement.valid_period_end)}
    </Typography>
  </Box>
);
